import { computed, reactive, watch } from 'vue'

import { del, get, put } from '@/api'
import { useAppConfig } from '@/composables/useAppConfig'
import { useStore } from '@/store'

// ── Types ────────────────────────────────────────────────────────────────────

export interface WorkflowHubEndpoint {
  url: string
  name: string
}

interface PublicWorkflowRepository {
  url: string
  label: string
  isWorkflowHub: boolean
}

interface ApiResult {
  success: boolean
  error?: string
}

export interface WorkflowHubInstance {
  url: string
  name: string
  apiKey: string
  savedApiKey: string
  isLoading: boolean
  isSaving: boolean
  readonly isConfigured: boolean
  readonly isModified: boolean
  readonly canSave: boolean
  updateApiKey(val: string): void
  getApiKey(): Promise<void>
  setApiKey(): Promise<ApiResult & { apiKey?: string }>
  removeApiKey(): Promise<ApiResult>
}

// ── Module-level state ────────────────────────────────────────────────────────

const state = reactive<{
  instances: Record<string, WorkflowHubInstance>
  isInitialized: boolean
}>({
  instances: {},
  isInitialized: false,
})

let initPromise: Promise<void> | null = null
let watcherCreated = false

// ── Instance factory ─────────────────────────────────────────────────────────

function createWorkflowHubInstance(
  endpoint: WorkflowHubEndpoint,
  username: string,
  initialApiKey = '',
): WorkflowHubInstance {
  const instance: WorkflowHubInstance = reactive<WorkflowHubInstance>({
    url: endpoint.url,
    name: endpoint.name,
    apiKey: initialApiKey,
    savedApiKey: initialApiKey,
    isLoading: false,
    isSaving: false,

    get isConfigured() {
      return !!this.savedApiKey
    },
    get isModified() {
      return this.apiKey !== this.savedApiKey
    },
    get canSave() {
      return !!this.apiKey && this.isModified
    },

    updateApiKey(val: string) {
      instance.apiKey = val
    },

    async getApiKey() {
      instance.isLoading = true
      try {
        const response = await get(`users/${username}/workflowhub_config/`, {
          params: { url: instance.url },
        })
        const key: string = response.data?.key || ''
        instance.apiKey = key
        instance.savedApiKey = key
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status
        if (![400, 404].includes(status ?? -1)) {
          console.error('WorkflowHub config fetch failed:', instance.url, err)
        }
        instance.apiKey = ''
        instance.savedApiKey = ''
      } finally {
        instance.isLoading = false
      }
    },

    async setApiKey(): Promise<ApiResult & { apiKey?: string }> {
      instance.isSaving = true
      try {
        await put(`users/${username}/workflowhub_config/`, {
          data: { url: instance.url, key: instance.apiKey },
        })
        instance.savedApiKey = instance.apiKey
        return { success: true, apiKey: instance.apiKey }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || 'Failed to save API key'
        return { success: false, error: msg }
      } finally {
        instance.isSaving = false
      }
    },

    async removeApiKey() {
      instance.isSaving = true
      try {
        await del(`users/${username}/workflowhub_config/`, {
          params: { url: instance.url },
        })
        instance.apiKey = ''
        instance.savedApiKey = ''
        return { success: true }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || 'Failed to remove configuration'
        return { success: false, error: msg }
      } finally {
        instance.isSaving = false
      }
    },
  })

  return instance
}

const normalizeUrl = (url: string) => url.replace(/\/+$/, '')

// ── Composable ────────────────────────────────────────────────────────────────

export function useWorkflowHub() {
  const { publicWorkflowRepositories, isConfigLoaded } = useAppConfig()
  const store = useStore()

  const configEndpoints = computed<WorkflowHubEndpoint[]>(() =>
    (publicWorkflowRepositories.value as PublicWorkflowRepository[])
      .filter((r) => r.isWorkflowHub)
      .map((r) => ({ url: r.url, name: r.label || r.url })),
  )

  const instanceList = computed(() => Object.values(state.instances))

  const configuredInstances = computed(() =>
    instanceList.value.filter((i) => i.isConfigured),
  )

  const unconfiguredEndpoints = computed(() =>
    configEndpoints.value.filter(
      (ep) => !state.instances[normalizeUrl(ep.url)]?.isConfigured,
    ),
  )

  function getOrCreateInstance(
    endpoint: WorkflowHubEndpoint,
  ): WorkflowHubInstance {
    const key = normalizeUrl(endpoint.url)
    if (state.instances[key]) return state.instances[key]
    const username: string = store.getters['auth/loggedInUser']?.username ?? ''
    const instance = createWorkflowHubInstance(endpoint, username)
    state.instances[key] = instance
    return instance
  }

  function doInit(
    endpoints: WorkflowHubEndpoint[],
    username: string,
  ): Promise<void> {
    if (state.isInitialized) return Promise.resolve()
    if (initPromise) return initPromise

    initPromise = (async () => {
      try {
        const results = await Promise.allSettled(
          endpoints.map((ep) =>
            get(`users/${username}/workflowhub_config/`, {
              params: { url: ep.url },
            })
              .then((r: { data?: { key?: string } }) => r.data?.key || '')
              .catch(() => ''),
          ),
        )

        endpoints.forEach((ep, i) => {
          const key = normalizeUrl(ep.url)
          if (!state.instances[key]) {
            const savedKey =
              results[i].status === 'fulfilled'
                ? (results[i] as PromiseFulfilledResult<string>).value
                : ''
            state.instances[key] = createWorkflowHubInstance(
              ep,
              username,
              savedKey,
            )
          }
        })

        state.isInitialized = true
      } catch (err) {
        console.error('WorkflowHub init failed:', err)
        initPromise = null
      }
    })()

    return initPromise
  }

  if (!watcherCreated) {
    watcherCreated = true
    watch(
      () => ({
        loaded: isConfigLoaded.value,
        username: store.getters['auth/loggedInUser']?.username as
          | string
          | undefined,
        endpoints: configEndpoints.value,
      }),
      ({ loaded, username, endpoints }) => {
        if (
          loaded &&
          username &&
          endpoints.length > 0 &&
          !state.isInitialized
        ) {
          doInit(endpoints, username)
        }
      },
      { immediate: true },
    )
  } else if (!state.isInitialized) {
    const username: string | undefined =
      store.getters['auth/loggedInUser']?.username
    if (username && configEndpoints.value.length > 0) {
      doInit(configEndpoints.value, username)
    }
  }

  return {
    isInitialized: computed(() => state.isInitialized),
    configEndpoints,
    configuredInstances,
    unconfiguredEndpoints,
    getOrCreateInstance,
  }
}
